import { TripContext } from 'contexts'
import { useContext } from 'react'
import { VerifiedMethod } from 'services/enums'
import { CatchWeight, LicensePlate, LicensePlateItem, LineItem, Order, OrderItem, OrderLicensePlate, ReconcileReason, RefusalReason, VerifyMultipleItemsReason } from 'services/types'
export const useLicensePlateService = () => {

    const tripContext = useContext(TripContext)

    const verifyLps = (lps: LicensePlate[], selectedReason: VerifyMultipleItemsReason, method: VerifiedMethod, reasonComment?: string): void => {
        let lpIds = lps.map(x => x.id)
        verifyLpsByIds(lpIds, method, selectedReason, reasonComment)
    }

    const verifyLpsByIds = (lpIds: number[], method: VerifiedMethod, selectedReason?: VerifyMultipleItemsReason, reasonComment?: string) => {
        let lpsToUpdate: (LicensePlate | undefined)[] = tripContext.licensePlates.filter(lp => lpIds.includes(lp.id))
        let updatedLps: LicensePlate[] = []
        lpsToUpdate && lpsToUpdate.forEach(lp => {
            lp && updatedLps.push({
                id: lp.id,
                stopId: lp.stopId,
                verifiedMethod: method,
                temperature: lp.temperature,
                verifiedMultipleReasonCode: selectedReason?.id ?? null,
                verifiedMultipleReasonComment: reasonComment ?? null,
                verifiedTime: new Date()
            })
        })

        tripContext.licensePlates.bulkPut(updatedLps)
    }

    const refuseLps = (lps: LicensePlate[], reconcileReason: ReconcileReason, refusalReason?: RefusalReason, refusalComment?: string): void => {
        let refusedLps: LicensePlate[] = []
        lps.forEach(lp => {
            refusedLps.push({
                id: lp.id,
                stopId: lp.stopId,
                verifiedMethod: VerifiedMethod.MassRefusal,
                temperature: lp.temperature,
                verifiedMultipleReasonCode: reconcileReason.id,
                verifiedMultipleReasonComment: refusalComment ?? null,
                verifiedTime: new Date()
            })
        })

        tripContext.licensePlates.bulkPut(refusedLps)
    }

    const verifyLp = (lpId: number, verifiedMethod: VerifiedMethod, reason?: VerifyMultipleItemsReason, reasonComment?: string): void => {
        const lp: LicensePlate | undefined = tripContext.licensePlates.get(lpId)
        if(lp) {
            tripContext.licensePlates.put({
                ...lp,
                verifiedMethod: verifiedMethod,
                verifiedTime: new Date(),
                verifiedMultipleReasonCode: reason?.id ?? null,
                verifiedMultipleReasonComment: reasonComment ?? null
            })
        }
    }

    const getLpsWithNonVerifiedItems = (licensePlates: LicensePlate[], orderLicensePlates: OrderLicensePlate[], licensePlateItems: LicensePlateItem[], orders: Order[], orderItems: OrderItem[]): LicensePlate[] => {
        let lps: LicensePlate[] = []
        licensePlates.forEach((lp: LicensePlate) => {
            const orderLps: OrderLicensePlate[] = orderLicensePlates.filter((olp: OrderLicensePlate) => olp.licensePlateId == lp.id)
            const lpItems: LicensePlateItem[] = licensePlateItems.filter((lpi: LicensePlateItem) => lpi.licensePlateId == lp.id)
            const lpOrders: Order[] = orders.filter((o: Order) => orderLps.some(olp => olp.orderId === o.id))

            const lpOrderItems: OrderItem[] = orderItems.filter((oi: OrderItem) => lpOrders.some(o => o.id == oi.orderId)
                && lpItems.some(lpi => lpi.lineItemId == oi.lineItemId)
                && oi.verifiedMethod == null
            )
            if (lpOrderItems.length > 0) {
                lps.push(lp)
            }
        })
        return lps
    }

    const deleteLicensePlates = (lpIds: number[]): void => {
        const lpItems: LicensePlateItem[] = tripContext.licensePlateItems.filter(lpItem => lpIds.includes(lpItem.licensePlateId))
        const orderLps: OrderLicensePlate[] = tripContext.orderLicensePlates.filter(orderLp => lpIds.includes(orderLp.licensePlateId))

        tripContext.licensePlates.bulkRemove(lpIds),
        tripContext.licensePlateItems.bulkRemove(lpItems.map(lpItem => lpItem.id)),
        tripContext.orderLicensePlates.bulkRemove(orderLps.map(orderLp => orderLp.id))
    }

    const areAllLpItemsVerified = (licensePlates: LicensePlate[], itemId: number): boolean => {
        let lpItems: LicensePlateItem[] = []
        const lineItem: LineItem = tripContext.lineItems.find(li => li.id == itemId) ?? {} as LineItem
        licensePlates.forEach(lp => {
            lpItems = [...lpItems, ...tripContext.licensePlateItems.filter(lpi => lpi.licensePlateId == lp.id && lpi.lineItemId == lineItem.id)]
        })

        // verify the item when only one items in the list
        if (lpItems.length === 1 && lpItems[0].isVerified === false) {

            let catchWeightItems: CatchWeight[] = getAllCatchWeightsByLicensePlateId(lpItems[0].licensePlateId, itemId)

            if (catchWeightItems.every(x => x.isVerified)) {
                lpItems[0].isVerified = true
                tripContext.licensePlateItems.put(lpItems[0])
            }
        }
        
        return (lpItems.filter(lpi => lpi.isVerified === undefined || lpi.isVerified === false).length === 0)
    }

    const getAllCatchWeightsByLicensePlateId = (licensePlateId: number, lineItemId: number): CatchWeight[] => {
        const licensePlate = tripContext.licensePlates.find(x => x.id == licensePlateId)
        const orders: Order[] = tripContext.orders.filter(o => o.stopId == licensePlate?.stopId)

        let items: OrderItem[] = []
        orders.forEach(order => {
            items = [...items, ...tripContext.orderItems.filter(oi => oi.orderId == order.id && oi.lineItemId == lineItemId)]
        })
        
        let catchWeightItems: CatchWeight[] = []
        items.forEach(orderItem => {
            catchWeightItems = [...catchWeightItems, ...tripContext.catchWeights.filter(cw => cw.orderItemId == orderItem.id)]
        })

        return catchWeightItems;
    }

    return {
        verifyLps: verifyLps,
        verifyLpsByIds: verifyLpsByIds,
        refuseLps: refuseLps,
        verifyLp: verifyLp,
        getLpsWithNonVerifiedItems: getLpsWithNonVerifiedItems,
        deleteLicensePlates: deleteLicensePlates,
        areAllLpItemsVerified: areAllLpItemsVerified
    }
}
